import React, { useState, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTimeoutFn } from "react-use";
import ReactMarkdown from "react-markdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faWindowMaximize,
  faWindowMinimize,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faMessageBot,
  faPaperPlaneTop,
  faUser,
  faEllipsis,
  faThumbsUp,
  faThumbsDown,
} from "@fortawesome/pro-solid-svg-icons";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { getUserAuth, getUser } from "../../recoil/selectors";
import { feedbackModalOpenState, patientState } from "../../recoil/atoms";
import {
  sendChatMessage,
  sendAgentChatMessage,
  startAgentHandoff,
  endAgentHandoff,
  fetchChatHistory,
  ChatbotResponse,
  ChatHistoryMessage,
  ChatHistoryResponse,
} from "./ChatBotService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { removeCookieAccessToken } from "../../utils/requestUtils";
import { LANDING_PAGE_URL } from "../../utils/urlConstants";

import {
  ChatbotWidgetContainer,
  ChatButton,
  ChatPopup,
  ChatHeader,
  ChatHeaderTitle,
  HeaderButton,
  ChatMessages,
  MessageWrapper,
  MessageIcon,
  MessageContent,
  Message,
  MessageSender,
  MessageTime,
  MessageDeliveryStatus,
  ChatForm,
  ChatInputLabel,
  ChatInputContainer,
  ChatInput,
  SendButton,
  PulsingIcon,
  TypingMessage,
  TruncatedMessage,
  ExpandedMessage,
  WarningMessage,
  LanguageSupportMessage,
  ShowMoreButton,
  FeedbackButtons,
  FeedbackButton,
  FeedbackPillsContainer,
  FeedbackPill,
  AgentStatusMessage,
  AgentConnectButton,
  AgentDeclineButton,
  AgentConversationBanner,
  AgentConversationBannerText,
  AgentDisconnectButton,
} from "./ChatBotWidget.styles";
import {
  AGENT_CHAT_RETRY_DELAY_MS,
  AGENT_NON_RESPONSE_TIMEOUT_MS,
  AGENT_FEEDBACK_PILL_CATEGORIES,
  FEEDBACK_EXEMPT_PATTERNS,
  FEEDBACK_PILL_CATEGORIES,
  FEEDBACK_TIMER_AGGRESSIVE_MS,
  FEEDBACK_TIMER_NORMAL_MS,
  MAX_AGENT_CONNECTION_FAILURES,
  MAX_PENDING_AGENT_MESSAGES,
  MESSAGE_MAX_LENGTH,
  TALKDESK_AUTOMATIC_FILTER,
  TRUNCATION_CHAR_LIMIT,
  USER_REJECTED_HANDOFF,
} from "./constants";

type AgentConnectionStatus =
  | "offered"
  | "connecting"
  | "connected"
  | "disconnected";

interface ChatUiMessage {
  id: string;
  content: string;
  sender: "user" | "bot";
  senderType?: "patient" | "bot" | "agent" | "system";
  senderName?: string;
  timestamp: string;
  feedback?: string;
  hasFeedbackButtons?: boolean;
  feedbackSubmitted?: "Positive" | "Negative";
  hasFeedbackPills?: boolean;
  usesAgentFeedbackPills?: boolean;
  isAgentHandoffOffer?: boolean;
  selectedPill?: string;
  agentDeliveryStatus?: "queued";
  idempotencyKey?: string;
}

const isQueuedAgentMessage = (message: ChatUiMessage): boolean =>
  message.agentDeliveryStatus === "queued";

const removeQueuedAgentMessages = (
  messagesToFilter: ChatUiMessage[],
): ChatUiMessage[] =>
  messagesToFilter.filter((message) => !isQueuedAgentMessage(message));

const sortMessagesByTimestamp = (
  messagesToSort: ChatUiMessage[],
): ChatUiMessage[] =>
  [...messagesToSort].sort(
    (firstMessage, secondMessage) =>
      Date.parse(firstMessage.timestamp) - Date.parse(secondMessage.timestamp),
  );

export const ChatbotWidget = ({
  isAggressiveChatbot,
}: {
  isAggressiveChatbot: boolean;
}): React.ReactElement => {
  // Chat UI
  const [chatState, setChatState] = useState<
    "closed" | "minimized" | "maximized"
  >("closed");
  const [shouldAutoMaximize, setShouldAutoMaximize] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );

  // Messages & input
  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEndingAgentConversation, setIsEndingAgentConversation] =
    useState(false);

  // Feedback
  const [hasGivenFeedback, setHasGivenFeedback] = useState(false);
  const [feedbackDelay, setFeedbackDelay] = useState<number | undefined>(
    undefined,
  );

  // Rate limiting
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus>("disconnected");
  const [agentConnectionFailureCount, setAgentConnectionFailureCount] =
    useState(0);
  const [hasAgentNonResponse, setHasAgentNonResponse] = useState(false);
  const [agentStartFailureStatus, setAgentStartFailureStatus] = useState("");

  // External state & hooks
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const location = useLocation();
  const authInfo = useRecoilValue(getUserAuth);
  const user = useRecoilValue(getUser);
  const setPatientInfo = useSetRecoilState(patientState);
  const isFeedbackModalOpen = useRecoilValue(feedbackModalOpenState);
  const agentConnectingStatus = t("chatbot.agentConnectingStatus");
  const agentConnectionIssueStatus = t("chatbot.agentConnectionIssueStatus");
  const agentConnectionReconnectingStatus = t(
    "chatbot.agentConnectionReconnectingStatus",
  );
  const agentConnectionTimeoutStatus = t(
    "chatbot.agentConnectionTimeoutStatus",
  );
  const agentMessageFailedStatus = t("chatbot.agentMessageFailedStatus");
  const agentConversationBannerMessage = t(
    "chatbot.agentConversationBannerMessage",
  );

  // Refs
  const chatbotWidgetRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const displayMessages = messages;
  const displayMessageCount = messages.length;
  const isAgentChatActive =
    agentConnectionStatus === "connecting" ||
    agentConnectionStatus === "connected";
  const queuedAgentMessages = messages.filter(isQueuedAgentMessage);
  const queuedAgentMessageCount = queuedAgentMessages.length;
  const nextQueuedAgentMessage = queuedAgentMessages[0];
  const isAgentMessageQueueFull =
    isAgentChatActive && queuedAgentMessageCount >= MAX_PENDING_AGENT_MESSAGES;

  const [, cancelFeedbackTimer] = useTimeoutFn(() => {
    // The timer hook always runs, so to "disable" it we set feedbackDelay to undefined
    if (feedbackDelay && !isAgentChatActive) {
      addFeedbackMessage();
    }
  }, feedbackDelay);

  // Update agent connection status according to state
  const getAgentCommunicationStatusMessage = () => {
    if (agentConnectionFailureCount >= MAX_AGENT_CONNECTION_FAILURES) {
      return agentConnectionIssueStatus;
    }

    if (agentConnectionFailureCount > 0 && isAgentChatActive) {
      return agentConnectionReconnectingStatus;
    }

    return undefined;
  };

  // Scroll on chat open, and whenever messages or status update
  const lastMessageContent = messages[messages.length - 1]?.content ?? "";
  const isShowingAgentConnectingStatus =
    agentConnectionStatus === "connecting" && agentConnectionFailureCount === 0;
  const isShowingAgentStartFailureStatus = agentConnectionStatus === "offered";
  let agentHandoffStatusMessage = "";
  if (isShowingAgentConnectingStatus) {
    agentHandoffStatusMessage = hasAgentNonResponse
      ? agentConnectionTimeoutStatus
      : agentConnectingStatus;
  } else if (isShowingAgentStartFailureStatus) {
    agentHandoffStatusMessage = agentStartFailureStatus;
  }
  const agentCommunicationStatusMessage =
    getAgentCommunicationStatusMessage() ?? "";
  const shouldScrollUpdate = `${chatState}|${lastMessageContent}|${agentHandoffStatusMessage}|${agentCommunicationStatusMessage}`;

  useEffect(() => {
    if (messages.length === 0 || chatState !== "maximized") {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [shouldScrollUpdate]);

  // Minimize the chat when the patient clicks outside the widget
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        chatState === "maximized" &&
        chatbotWidgetRef.current &&
        !chatbotWidgetRef.current.contains(e.target as Node)
      ) {
        minimizeChat("click_outside");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [chatState]);

  // Maintain textarea size even after chat is minimized/closed
  useEffect(() => {
    if (chatState === "maximized") {
      const textarea = document.getElementById(
        "chat-input",
      ) as HTMLTextAreaElement;
      if (textarea) {
        if (inputMessage) {
          textarea.style.height = "auto";
          textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
        } else {
          textarea.style.height = "38px";
        }
      }
    }
  }, [chatState, inputMessage]);

  // Fetch chat history when auth is available
  useEffect(() => {
    loadChatHistory();
  }, [authInfo]);

  // Retry agent handoff startup after transient failures
  useEffect(() => {
    if (
      agentConnectionStatus !== "connecting" ||
      !authInfo?.access_token ||
      agentConnectionFailureCount === 0
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      attemptStartAgentHandoff().catch((error) => {
        console.error("Agent handoff start retry error:", error);
      });
    }, AGENT_CHAT_RETRY_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [
    agentConnectionStatus,
    authInfo?.access_token,
    agentConnectionFailureCount,
  ]);

  // Sync active agent chats by fetching agent messages.
  useEffect(() => {
    if (!isAgentChatActive || !authInfo?.access_token) {
      return;
    }

    const intervalId = setInterval(() => {
      loadChatHistory();
    }, AGENT_CHAT_RETRY_DELAY_MS);

    return () => clearInterval(intervalId);
  }, [isAgentChatActive, authInfo?.access_token, agentConnectionFailureCount]);

  // Retry sending message to agent if it failed
  useEffect(() => {
    if (
      !isAgentChatActive ||
      !authInfo?.access_token ||
      !nextQueuedAgentMessage
    ) {
      return;
    }

    const retryDelay =
      agentConnectionFailureCount > 0 ? AGENT_CHAT_RETRY_DELAY_MS : 0;
    const timeoutId = setTimeout(() => {
      sendPendingAgentMessage(nextQueuedAgentMessage).catch((error) => {
        console.error("Agent chat queue processing error:", error);
      });
    }, retryDelay);

    return () => clearTimeout(timeoutId);
  }, [
    isAgentChatActive,
    authInfo?.access_token,
    nextQueuedAgentMessage?.id,
    agentConnectionFailureCount,
  ]);

  // Surface the connection issue status if no agent connects within the timeout
  useEffect(() => {
    if (agentConnectionStatus !== "connecting") {
      setHasAgentNonResponse(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setHasAgentNonResponse(true);
    }, AGENT_NON_RESPONSE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [agentConnectionStatus]);

  // Auto-open the chatbot only on the dashboard and only once per mount
  useEffect(() => {
    const isOnDashboard = location.pathname === LANDING_PAGE_URL;
    if (
      !shouldAutoMaximize ||
      !isOnDashboard ||
      deviceType === "mobile" ||
      isFeedbackModalOpen !== false
    )
      return;

    if (displayMessageCount > 1 && chatState === "closed") {
      setChatState("maximized");
      setShouldAutoMaximize(false);
      return;
    }

    if (!isAggressiveChatbot) return;

    const timer = setTimeout(() => {
      if (chatState === "closed") {
        setChatState("maximized");
        setShouldAutoMaximize(false);

        // Track that the aggressive chatbot actually triggered
        if (authInfo?.access_token) {
          TrackAnalyticsEvent(
            AnalyticsEventNames.ChatbotAggressiveTriggered,
            authInfo.access_token,
            undefined,
          );
        }
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [
    shouldAutoMaximize,
    chatState,
    location.pathname,
    displayMessageCount,
    deviceType,
    isAggressiveChatbot,
    isFeedbackModalOpen,
    authInfo?.access_token,
  ]);

  // Keep feedback prompts from appearing once any feedback exists in history
  useEffect(() => {
    const userHasGivenFeedback = messages.some(
      (msg) => msg.feedback || msg.feedbackSubmitted,
    );
    setHasGivenFeedback(userHasGivenFeedback);
  }, [messages]);

  // Replace any old feedback prompt before appending the current one
  const addFeedbackMessage = () => {
    const feedbackMessage = createFeedbackRequestMessage({
      id: "feedback-request",
      timestamp: new Date().toISOString(),
    });

    setMessages((prev) => {
      const withoutOldFeedback = prev.filter((msg) => !msg.hasFeedbackButtons);
      return [...withoutOldFeedback, feedbackMessage];
    });
  };

  // Convert server history records into the local message shape
  const buildUiMessage = (
    historyMessage: ChatHistoryMessage,
    agentEngaged: boolean = false,
  ): ChatUiMessage => {
    // Custom rendering for feedback showing thumb selection
    if (
      historyMessage.feedback === "Positive" ||
      historyMessage.feedback === "Negative"
    ) {
      return createFeedbackRequestMessage({
        id: `${historyMessage.id}-feedback-request`,
        timestamp: historyMessage.sent_at,
        feedback: historyMessage.feedback,
      });
    }

    // Custom rendering for pill selection
    if (historyMessage.feedback === "Pill") {
      return createPillSelectionMessage({
        id: `${historyMessage.id}-pill-selection`,
        timestamp: historyMessage.sent_at,
        selectedPill: historyMessage.body,
        agentEngaged,
      });
    }

    let messageContent = historyMessage.body;
    if (historyMessage.id.endsWith("-started")) {
      messageContent = t("chatbot.agentConnectedMessage");
    } else if (historyMessage.id.endsWith("-ended")) {
      messageContent = t("chatbot.agentDisconnectedMessage");
    }

    // Default rendering for normal text messages
    return {
      id: historyMessage.id,
      content: messageContent,
      sender: historyMessage.sender_type === "patient" ? "user" : "bot",
      senderType: historyMessage.sender_type,
      senderName: historyMessage.sender_name,
      timestamp: historyMessage.sent_at,
      isAgentHandoffOffer: historyMessage.is_agent_handoff_offer,
    };
  };

  // Convert chatbot API responses into the local message shape
  const createMessage = (
    response: ChatbotResponse,
    isUser: boolean = false,
  ): ChatUiMessage => ({
    id: `faq-${isUser ? "user" : "bot"}-${response.message_id}`,
    content: isUser ? response.user_message : response.bot_response,
    sender: isUser ? "user" : "bot",
    senderType: response.agent_chat_active ? "system" : undefined,
    timestamp: isUser
      ? response.user_message_timestamp
      : response.bot_response_timestamp,
    feedback: response.feedback,
    isAgentHandoffOffer: !isUser && response.agent_handoff_offered,
  });

  // Render feedback history as an interactive-looking request message
  const createFeedbackRequestMessage = ({
    id,
    timestamp,
    feedback,
  }: {
    id: string;
    timestamp: string;
    feedback?: string;
  }): ChatUiMessage => ({
    id,
    content: t("chatbot.feedbackRequest"),
    sender: "bot",
    timestamp,
    hasFeedbackButtons: true,
    feedbackSubmitted: feedback as "Positive" | "Negative" | undefined,
    feedback,
  });

  // Render feedback category history as a selected pill prompt
  const createPillSelectionMessage = ({
    id,
    timestamp,
    selectedPill,
    agentEngaged = false,
  }: {
    id: string;
    timestamp: string;
    selectedPill?: string;
    agentEngaged?: boolean;
  }): ChatUiMessage => {
    const agentPillSelected = selectedPill === "chatbot.pill.agentNotHelpful";
    // Bot pills only exist in the default variant, so a prior selection of one
    // must keep the default variant for it to render and stay highlighted.
    const botPillSelected =
      selectedPill === "chatbot.pill.notWhatIAsked" ||
      selectedPill === "chatbot.pill.stillConfused";

    return {
      id,
      content: "",
      sender: "bot",
      timestamp,
      hasFeedbackPills: true,
      usesAgentFeedbackPills:
        agentPillSelected || (agentEngaged && !botPillSelected),
      selectedPill,
    };
  };

  // Display timestamps
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleDateString("en-US", options);
  };

  // Prefer the live agent's first name, otherwise fall back by sender type
  const getSenderName = (message: ChatUiMessage): string => {
    if (message.senderType === "agent") {
      if (message.senderName?.includes(TALKDESK_AUTOMATIC_FILTER) ?? false) {
        return t("chatbot.automaticMessageSenderName");
      }

      return (
        message.senderName?.split(" ")[0] || t("chatbot.agentSupportSenderName")
      );
    }

    if (message.sender === "bot") return "Mira";

    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }

    return user?.first_name || t("chatbot.you");
  };

  // Track expanded long bot messages by id
  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Only bot responses are truncated because patient messages are already length-limited
  const shouldTruncateMessage = (content: string, sender: string): boolean =>
    sender === "bot" && content.length > TRUNCATION_CHAR_LIMIT;

  const isMessageExpanded = (messageId: string): boolean =>
    expandedMessages.has(messageId);

  const renderMessageContent = (message: ChatUiMessage): React.ReactNode => {
    if (message.sender === "bot") {
      return <ReactMarkdown>{message.content}</ReactMarkdown>;
    }

    return message.content;
  };

  // Load history, merge local-only agent messages, and initialize the greeting if empty
  const loadChatHistory = async () => {
    if (!authInfo?.access_token) {
      return;
    }

    try {
      const response = await fetchChatHistory(authInfo.access_token);

      if (!response.ok) {
        if (isAgentChatActive) {
          recordAgentConnectionFailure();
        }
        console.error("Failed to fetch chat history");
        return;
      }

      // Determine agent connection status based on the response
      const data: ChatHistoryResponse = await response.json();
      const hasOpenAgentConversation =
        data.agent_chat_status !== "disconnected";
      const isStartingAgentHandoff =
        agentConnectionStatus === "connecting" && !hasOpenAgentConversation;
      if (
        queuedAgentMessageCount === 0 &&
        (!isStartingAgentHandoff || hasOpenAgentConversation)
      ) {
        setAgentConnectionFailureCount(0);
      }
      const nonUserMessages = data.messages.filter(
        (message) => message.sender_type !== "patient",
      );
      const latestHistoryMessage = nonUserMessages[nonUserMessages.length - 1];
      const conversationJustEnded =
        latestHistoryMessage?.id.endsWith("-ended") ?? false;
      setAgentConnectionStatus((currentStatus) => {
        // Accept disconnect status if we've recently disconnected
        if (conversationJustEnded && currentStatus === "connecting") {
          return "disconnected";
        }
        // Ignore disconnected status if we're waiting to connect
        if (
          data.agent_chat_status === "disconnected" &&
          (currentStatus === "connecting" || currentStatus === "offered")
        ) {
          return currentStatus;
        }
        return data.agent_chat_status;
      });
      setMessages((prev) => {
        const historyMessages = data.messages.map((message) =>
          buildUiMessage(message, data.agent_chat_engaged ?? false),
        );
        // Preserve optimistic messages that are not returned by history.
        const localAgentMessages = prev.filter(isQueuedAgentMessage);
        const nextMessages = [...historyMessages, ...localAgentMessages];

        if (
          nextMessages.length > 0 ||
          data.agent_chat_status !== "disconnected"
        ) {
          return sortMessagesByTimestamp(nextMessages);
        }

        return [
          {
            id: "1",
            content: t("chatbot.welcomeMessage"),
            sender: "bot",
            timestamp: new Date().toISOString(),
          },
        ];
      });

      if (conversationJustEnded) {
        addFeedbackMessage();
      }
    } catch (error) {
      if (isAgentChatActive) {
        recordAgentConnectionFailure();
      }
      console.error("Error fetching chat history:", error);
    }
  };

  // Send the oldest queued agent message and reconcile it with the server response
  const sendPendingAgentMessage = async (
    pendingMessage: ChatUiMessage,
  ): Promise<boolean> => {
    if (!authInfo?.access_token) {
      return false;
    }

    try {
      // Attempt to send the next queued message to the live-agent endpoint
      const response = await sendAgentChatMessage(
        pendingMessage.content,
        authInfo.access_token,
        pendingMessage.idempotencyKey ?? "",
      );

      if (response.status === 401 || response.status === 403) {
        handleAuthenticationFailure();
        return false;
      }

      // Any non-success response leaves the message in the queue for retry handling
      if (!response.ok) {
        throw new Error("Failed to send agent chat message");
      }

      // On success, convert the local queued message into the server-shaped message shown in history.
      const data = await response.json();
      setAgentConnectionFailureCount(0);
      setMessages((prev) => {
        const deliveredMessageId = `agent-${data.id}`;
        if (prev.some((message) => message.id === deliveredMessageId)) {
          return prev.filter((message) => message.id !== pendingMessage.id);
        }
        return prev.map((message) =>
          message.id === pendingMessage.id
            ? {
                ...message,
                id: deliveredMessageId,
                content: data.body || pendingMessage.content,
                senderType: data.sender_type || "patient",
                timestamp: data.sent_at || pendingMessage.timestamp,
                agentDeliveryStatus: undefined,
              }
            : message,
        );
      });
      return true;
    } catch (error) {
      console.error("Agent chat send error:", error);

      recordAgentConnectionFailure();
      return false;
    }
  };

  const maximizeChat = () => {
    setChatState("maximized");
    setShouldAutoMaximize(false);
    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotMaximize,
      authInfo?.access_token,
      undefined,
      {
        source: "chat_button",
      },
    );
  };

  const minimizeChat = (source: string) => {
    setChatState("minimized");
    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotMinimize,
      authInfo?.access_token,
      undefined,
      {
        source,
      },
    );
  };

  const closeChat = (source: string) => {
    setChatState("closed");
    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotClose,
      authInfo?.access_token,
      undefined,
      {
        source,
      },
    );
  };

  const handleFeedbackSubmit = async (feedback: "Positive" | "Negative") => {
    if (!authInfo?.access_token || !user) {
      return;
    }

    setFeedbackDelay(undefined);
    cancelFeedbackTimer();

    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotFeedbackSubmitted,
      authInfo.access_token,
      undefined,
      {
        feedback_type: feedback,
      },
    );

    try {
      // Submit feedback to backend which will construct the response
      const response = await sendChatMessage(
        "",
        authInfo.access_token,
        feedback,
      );

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      const data: ChatbotResponse = await response.json();

      // Mark the feedback request as submitted and add backend response
      setMessages((prev) => {
        const updated = prev.map((msg) =>
          msg.hasFeedbackButtons
            ? { ...msg, feedbackSubmitted: feedback }
            : msg,
        );

        // Negative feedback shows pill selection for more detail (if enabled)
        if (feedback === "Negative") {
          const pillMessage = createPillSelectionMessage({
            id: `pills-${Date.now()}`,
            timestamp: data.bot_response_timestamp,
            agentEngaged: data.agent_chat_engaged ?? false,
          });
          return [...updated, pillMessage];
        }

        // Positive feedback shows thank you message from backend
        return [...updated, createMessage(data)];
      });

      // Update state to prevent feedback from showing again
      setHasGivenFeedback(true);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  const handlePillSelect = async (category: string) => {
    if (!authInfo?.access_token || !user) {
      return;
    }

    // Optimistically mark the pill as selected (instant UI feedback)
    setMessages((prev) =>
      prev.map((msg) =>
        msg.hasFeedbackPills ? { ...msg, selectedPill: category } : msg,
      ),
    );

    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotFeedbackPillSelected,
      authInfo.access_token,
      undefined,
      {
        category,
      },
    );

    try {
      // Submit pill selection to backend which will record and respond
      const response = await sendChatMessage(
        category,
        authInfo.access_token,
        "Pill",
      );

      if (!response.ok) {
        return;
      }

      const data: ChatbotResponse = await response.json();

      // Add response from backend and prompt for optional written feedback
      setMessages((prev) => {
        const combinedMessage: ChatUiMessage = {
          id: `pill-response-${Date.now()}`,
          content: `${data.bot_response}\n\n${t("chatbot.writtenFeedbackPrompt")}`,
          sender: "bot",
          timestamp: data.bot_response_timestamp,
        };
        return [...prev, combinedMessage];
      });
    } catch {
      // Silently ignore - UI already updated optimistically
    }
  };

  const handleAuthenticationFailure = () => {
    // Clear Authentication State
    removeCookieAccessToken();
    setPatientInfo(undefined);
  };

  const recordAgentConnectionFailure = () => {
    setAgentConnectionFailureCount(
      (currentFailureCount) => currentFailureCount + 1,
    );
  };

  // Count connection failures until the UI should return to the handoff offer
  const recordAgentHandoffStartFailure = () => {
    const nextFailureCount = agentConnectionFailureCount + 1;
    if (nextFailureCount >= MAX_AGENT_CONNECTION_FAILURES) {
      setAgentConnectionStatus("offered");
      setAgentStartFailureStatus(agentConnectionIssueStatus);
      setAgentConnectionFailureCount(0);
      return;
    }
    setAgentConnectionFailureCount(nextFailureCount);
  };

  // Start the live-agent conversation; retries are scheduled by the effect above
  const attemptStartAgentHandoff = async (): Promise<void> => {
    if (!authInfo?.access_token) return;

    setIsLoading(true);
    try {
      const response = await startAgentHandoff(authInfo.access_token);
      if (response.ok) {
        setAgentConnectionFailureCount(0);
        setAgentStartFailureStatus("");
        return;
      }

      console.error("Failed to start agent handoff");
    } catch (error) {
      console.error("Error starting agent handoff:", error);
    } finally {
      setIsLoading(false);
    }

    recordAgentHandoffStartFailure();
  };

  // Accept the handoff offer and begin connecting to a live agent
  const handleAgentConnect = () => {
    if (!authInfo?.access_token || isLoading) return;

    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotAgentHandoffAccepted,
      authInfo.access_token,
    );

    setAgentConnectionFailureCount(0);
    setAgentStartFailureStatus("");
    setAgentConnectionStatus("connecting");
    attemptStartAgentHandoff().catch((error) => {
      console.error("Agent handoff start error:", error);
    });
  };

  // Record the patient declining handoff as a normal chatbot message
  const handleAgentDecline = async () => {
    if (!authInfo?.access_token || isLoading) return;

    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotAgentHandoffDeclined,
      authInfo.access_token,
    );

    setAgentStartFailureStatus("");
    setAgentConnectionStatus("disconnected");
    setIsLoading(true);
    try {
      const response = await sendChatMessage(
        USER_REJECTED_HANDOFF,
        authInfo.access_token,
      );
      if (!response.ok) {
        return;
      }

      const data: ChatbotResponse = await response.json();
      setMessages((prev) => [...prev, createMessage(data)]);
    } catch (error) {
      console.error("Error declining agent handoff:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // End the live-agent session and refresh history to show the backend's final state
  const endActiveAgentConversation = async () => {
    if (!authInfo?.access_token || isEndingAgentConversation) {
      return;
    }

    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotAgentHandoffDisconnected,
      authInfo.access_token,
    );

    setIsEndingAgentConversation(true);
    try {
      const response = await endAgentHandoff(authInfo.access_token);
      if (!response.ok) {
        console.error("Failed to end agent handoff");
        return;
      }

      setAgentConnectionStatus("disconnected");
      setMessages(removeQueuedAgentMessages);
      await loadChatHistory();
    } catch (error) {
      console.error("Error ending agent handoff:", error);
    } finally {
      setIsEndingAgentConversation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const message = inputMessage.trim();
    if (!message || isLoading || !authInfo?.access_token) return;

    // Silently block while rate limited — timer from 429 handler will reset this
    if (isRateLimited) return;

    setFeedbackDelay(undefined);
    cancelFeedbackTimer();

    // Add user message to the chat
    const userMessage: ChatUiMessage = {
      id: isAgentChatActive
        ? `agent-local-${Date.now()}-${displayMessageCount}`
        : `user-${Date.now()}`,
      content: message,
      sender: "user",
      timestamp: new Date().toISOString(),
    };

    if (isAgentChatActive) {
      setMessages((prev) => [
        ...prev,
        {
          ...userMessage,
          agentDeliveryStatus: "queued",
          idempotencyKey: crypto.randomUUID(),
        },
      ]);
    } else {
      setMessages((prev) => [...prev, userMessage]);
    }

    // Reset input and set loading state
    setInputMessage("");
    setIsLoading(true);

    // Track message sent event
    TrackAnalyticsEvent(
      AnalyticsEventNames.ChatbotMessageSent,
      authInfo?.access_token,
      undefined,
    );

    if (!isAgentChatActive) {
      const typingMessage: ChatUiMessage = {
        id: "typing-indicator",
        content: "",
        sender: "bot",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, typingMessage]);
    }

    const fallbackMessage = t("chatbot.fallbackMessage");

    try {
      // Agent chats are sent by the queue effect; FAQ chats continue through this request.
      if (isAgentChatActive) {
        return;
      }

      const response = await sendChatMessage(message, authInfo.access_token);

      // Check for authentication failures (401/403)
      if (response.status === 401 || response.status === 403) {
        handleAuthenticationFailure();

        // The ProtectedRoute will automatically redirect to login and save current path
        return;
      }

      // Backend rate limit — show message and block until Retry-After expires
      if (response.status === 429) {
        setIsRateLimited(true);
        setFeedbackDelay(undefined);
        cancelFeedbackTimer();

        const retryAfter =
          parseInt(response.headers.get("Retry-After") ?? "", 10) || 60;
        setTimeout(() => {
          setIsRateLimited(false);
        }, retryAfter * 1000);

        setMessages((prev) => {
          const filtered = prev.filter((msg) => msg.id !== "typing-indicator");
          return [
            ...filtered,
            {
              id: `rate-limit-${Date.now()}`,
              content: t("chatbot.rateLimitMessage"),
              sender: "bot" as const,
              timestamp: new Date().toISOString(),
            },
          ];
        });
        return;
      }

      const data = await response.json();

      // Use fallback if the API didn't return a response
      const apiContent = data.bot_response || fallbackMessage;

      // Build response from API data
      const botResponse = createMessage({
        ...data,
        bot_response: apiContent,
      });
      setAgentConnectionFailureCount(0);
      if (data.agent_chat_active) {
        setAgentConnectionStatus("connecting");
      } else if (data.agent_handoff_offered) {
        setAgentStartFailureStatus("");
        setAgentConnectionStatus("offered");
      }

      // Replace typing indicator with bot response
      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== "typing-indicator");
        return [...filtered, botResponse];
      });

      if (!hasGivenFeedback && !data.agent_chat_active) {
        const shouldSkipFeedback = FEEDBACK_EXEMPT_PATTERNS.some((pattern) =>
          pattern.test(apiContent),
        );

        if (shouldSkipFeedback) {
          return;
        }

        const isThankYouResponse = /you'?re welcome/i.test(apiContent);

        if (isThankYouResponse) {
          addFeedbackMessage();
        } else {
          const delay = isAggressiveChatbot
            ? FEEDBACK_TIMER_AGGRESSIVE_MS
            : FEEDBACK_TIMER_NORMAL_MS;
          setFeedbackDelay(delay);
        }
      }
    } catch (error) {
      // Use fallback if we ran into a frontend error
      const botResponse: ChatUiMessage = {
        id: `error-${Date.now()}`,
        content: fallbackMessage,
        sender: "bot",
        senderType: isAgentChatActive ? "system" : "bot",
        timestamp: new Date().toISOString(),
      };

      // Replace typing indicator with fallback
      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== "typing-indicator");
        return [...filtered, botResponse];
      });

      console.error("Chat API error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Render icon user can click to begin chat
   */
  const renderChatButton = () => (
    <ChatbotWidgetContainer ref={chatbotWidgetRef}>
      <ChatButton onClick={maximizeChat} aria-label={t("chatbot.openChat")}>
        <FontAwesomeIcon icon={faMessageBot} />
      </ChatButton>
    </ChatbotWidgetContainer>
  );

  /**
   * Render header with title and control buttons
   */
  const renderChatHeader = () => (
    <ChatHeader
      onClick={
        chatState === "minimized"
          ? maximizeChat
          : () => minimizeChat("minimize_button")
      }
    >
      <ChatHeaderTitle>
        <FontAwesomeIcon icon={faMessageBot} />
        Mira
      </ChatHeaderTitle>
      <div>
        <HeaderButton
          onClick={
            chatState === "minimized"
              ? maximizeChat
              : () => minimizeChat("minimize_button")
          }
          aria-label={
            chatState === "minimized"
              ? t("chatbot.maximizeChat")
              : t("chatbot.minimizeChat")
          }
        >
          <FontAwesomeIcon
            icon={
              chatState === "minimized" ? faWindowMaximize : faWindowMinimize
            }
          />
        </HeaderButton>
        <HeaderButton
          onClick={(e) => {
            e.stopPropagation();
            closeChat("close_button");
          }}
          aria-label={t("chatbot.closeChat")}
        >
          <FontAwesomeIcon icon={faXmark} />
        </HeaderButton>
      </div>
    </ChatHeader>
  );

  /**
   * Render disclaimer
   */
  const renderWarningMessage = () => (
    <WarningMessage>
      {t("chatbot.warning")}
      <br />
      <strong>{t("chatbot.consent")}</strong>
    </WarningMessage>
  );

  /**
   * Render language support message
   */
  const renderLanguageSupportMessage = () => (
    <LanguageSupportMessage>
      {t("chatbot.languageSupport.prefix")}{" "}
      <strong>{t("chatbot.languageSupport.english")}</strong>{" "}
      {t("chatbot.languageSupport.and")}{" "}
      <strong>{t("chatbot.languageSupport.spanish")}</strong>
    </LanguageSupportMessage>
  );

  const renderAgentConversationBanner = () => (
    <AgentConversationBanner>
      <AgentConversationBannerText>
        {agentConversationBannerMessage}
      </AgentConversationBannerText>
      <AgentDisconnectButton
        onClick={endActiveAgentConversation}
        disabled={isEndingAgentConversation}
      >
        {t("chatbot.agentDisconnectButton")}
      </AgentDisconnectButton>
    </AgentConversationBanner>
  );

  const renderAgentCommunicationStatusMessage = () => {
    const statusMessage = getAgentCommunicationStatusMessage();
    if (!statusMessage) {
      return null;
    }

    return (
      <AgentStatusMessage
        $isItalic={
          statusMessage === agentConnectionReconnectingStatus ||
          statusMessage === agentConnectionIssueStatus
        }
      >
        {statusMessage}
      </AgentStatusMessage>
    );
  };

  const renderAgentHandoffStatusMessage = () => {
    if (!agentHandoffStatusMessage) {
      return null;
    }

    return (
      <AgentStatusMessage
        $isItalic={agentHandoffStatusMessage === agentConnectingStatus}
      >
        {agentHandoffStatusMessage}
      </AgentStatusMessage>
    );
  };

  /**
   * Render special ellipsis message for when the bot is "thinking"
   */
  const renderTypingIndicator = () => (
    <MessageWrapper isUser={false}>
      <MessageIcon isUser={false}>
        <FontAwesomeIcon icon={faMessageBot} />
      </MessageIcon>
      <MessageContent isUser={false}>
        <MessageSender isUser={false}>Mira</MessageSender>
        <TypingMessage isUser={false}>
          <PulsingIcon>
            <FontAwesomeIcon icon={faEllipsis} />
          </PulsingIcon>
        </TypingMessage>
        <MessageTime isUser={false}>&nbsp;</MessageTime>
      </MessageContent>
    </MessageWrapper>
  );

  /**
   * Render a single chat message with icon and timestamp
   */
  const renderChatMessage = (message: ChatUiMessage) => {
    if (message.senderType === "system") {
      // System messages use the agent status styling instead of normal chat bubbles.
      return (
        <AgentStatusMessage
          $isItalic={
            message.content === agentConnectingStatus ||
            message.content === agentConnectionIssueStatus
          }
        >
          {message.content}
        </AgentStatusMessage>
      );
    }

    const isUser = message.sender === "user";
    const isAgent = message.senderType === "agent";
    const isLocalPendingAgentMessage = isQueuedAgentMessage(message);
    const shouldShowMessageDeliveryFailure =
      isLocalPendingAgentMessage && agentConnectionFailureCount > 0;

    return (
      <MessageWrapper isUser={isUser}>
        <MessageIcon isUser={isUser} $isAgent={isAgent}>
          <FontAwesomeIcon icon={isUser || isAgent ? faUser : faMessageBot} />
        </MessageIcon>
        <MessageContent isUser={isUser}>
          <MessageSender isUser={isUser}>
            {getSenderName(message)}
          </MessageSender>
          {(() => {
            // Keep long bot answers readable while preserving full markdown content.
            if (shouldTruncateMessage(message.content, message.sender)) {
              const MessageComponent = isMessageExpanded(message.id)
                ? ExpandedMessage
                : TruncatedMessage;
              const buttonText = isMessageExpanded(message.id)
                ? t("chatbot.showLess")
                : t("chatbot.showMore");

              return (
                <MessageComponent isUser={false} $isAgent={isAgent}>
                  {renderMessageContent(message)}
                  <ShowMoreButton
                    onClick={() => toggleMessageExpansion(message.id)}
                  >
                    {buttonText}
                  </ShowMoreButton>
                </MessageComponent>
              );
            }

            if (message.hasFeedbackButtons) {
              // Feedback request messages render thumbs instead of plain text only.
              return (
                <Message isUser={isUser} $isAgent={isAgent}>
                  {renderMessageContent(message)}
                  <FeedbackButtons>
                    <FeedbackButton
                      onClick={() => handleFeedbackSubmit("Positive")}
                      disabled={!!message.feedbackSubmitted}
                      className={
                        message.feedbackSubmitted === "Positive"
                          ? "selected"
                          : ""
                      }
                      aria-label={t("chatbot.thumbsUp")}
                    >
                      <FontAwesomeIcon icon={faThumbsUp} />
                    </FeedbackButton>
                    <FeedbackButton
                      onClick={() => handleFeedbackSubmit("Negative")}
                      disabled={!!message.feedbackSubmitted}
                      className={
                        message.feedbackSubmitted === "Negative"
                          ? "selected"
                          : ""
                      }
                      aria-label={t("chatbot.thumbsDown")}
                    >
                      <FontAwesomeIcon icon={faThumbsDown} />
                    </FeedbackButton>
                  </FeedbackButtons>
                </Message>
              );
            }

            if (message.isAgentHandoffOffer) {
              // The same handoff bubble can be disabled while connection attempts are in flight.
              const isAgentHandoffDisabled =
                isLoading ||
                agentConnectionStatus !== "offered" ||
                agentConnectionFailureCount >= MAX_AGENT_CONNECTION_FAILURES;

              return (
                <>
                  <Message isUser={isUser} $isAgent={isAgent}>
                    {renderMessageContent(message)}
                    <FeedbackButtons>
                      <AgentDeclineButton
                        onClick={handleAgentDecline}
                        disabled={isAgentHandoffDisabled}
                      >
                        No
                      </AgentDeclineButton>
                      <AgentConnectButton
                        onClick={handleAgentConnect}
                        disabled={isAgentHandoffDisabled}
                      >
                        {t("chatbot.agentConnectButton")}
                      </AgentConnectButton>
                    </FeedbackButtons>
                  </Message>
                </>
              );
            }

            if (message.hasFeedbackPills) {
              return (
                <>
                  <Message isUser={isUser} $isAgent={isAgent}>
                    <strong>{t("chatbot.pillPromptTitle")}</strong>
                    <br />
                    {t("chatbot.pillPromptSubtitle")}
                  </Message>
                  <FeedbackPillsContainer>
                    {(message.usesAgentFeedbackPills
                      ? AGENT_FEEDBACK_PILL_CATEGORIES
                      : FEEDBACK_PILL_CATEGORIES
                    ).map((category) => (
                      <FeedbackPill
                        key={category}
                        onClick={() => handlePillSelect(category)}
                        disabled={!!message.selectedPill}
                        className={
                          message.selectedPill === category ? "selected" : ""
                        }
                      >
                        {t(category)}
                      </FeedbackPill>
                    ))}
                  </FeedbackPillsContainer>
                </>
              );
            }

            return (
              <Message
                isUser={isUser}
                isQueued={isLocalPendingAgentMessage}
                $isAgent={isAgent}
              >
                {renderMessageContent(message)}
              </Message>
            );
          })()}
          {shouldShowMessageDeliveryFailure ? (
            <MessageDeliveryStatus isUser={isUser}>
              {agentMessageFailedStatus}
            </MessageDeliveryStatus>
          ) : (
            <MessageTime isUser={isUser}>
              {formatTime(message.timestamp)}
            </MessageTime>
          )}
        </MessageContent>
      </MessageWrapper>
    );
  };

  /**
   * Render input form with send button
   */
  const renderChatForm = () => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const form = e.currentTarget.closest("form");
        if (form) {
          form.requestSubmit();
        }
      }
    };

    return (
      <ChatForm onSubmit={handleSubmit}>
        <ChatInputLabel htmlFor="chat-input">
          {t("chatbot.yourMessage")}
        </ChatInputLabel>
        <ChatInputContainer>
          <ChatInput
            as="textarea"
            id="chat-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder=""
            autoComplete="off"
            maxLength={MESSAGE_MAX_LENGTH}
            rows={1}
          />
          <SendButton
            type="submit"
            aria-label={t("chatbot.sendMessage")}
            disabled={
              isLoading ||
              !inputMessage.trim() ||
              isRateLimited ||
              isAgentMessageQueueFull
            }
          >
            <FontAwesomeIcon icon={faPaperPlaneTop} />
          </SendButton>
        </ChatInputContainer>
      </ChatForm>
    );
  };

  return (
    <>
      {chatState === "closed" && renderChatButton()}

      {(chatState === "minimized" || chatState === "maximized") && (
        <ChatbotWidgetContainer ref={chatbotWidgetRef}>
          <ChatPopup
            deviceType={deviceType}
            isMinimized={chatState === "minimized"}
          >
            {renderChatHeader()}

            {chatState === "maximized" && (
              <>
                {agentConnectionStatus === "connected" &&
                  renderAgentConversationBanner()}
                <ChatMessages
                  onWheel={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {agentConnectionStatus !== "connected" &&
                    renderWarningMessage()}
                  {agentConnectionStatus !== "connected" &&
                    renderLanguageSupportMessage()}
                  {displayMessages.map((message) => (
                    <div key={message.id}>
                      {message.id === "typing-indicator"
                        ? renderTypingIndicator()
                        : renderChatMessage(message)}
                    </div>
                  ))}
                  {renderAgentHandoffStatusMessage()}
                  {renderAgentCommunicationStatusMessage()}
                  <div ref={messagesEndRef} />
                </ChatMessages>

                {renderChatForm()}
              </>
            )}
          </ChatPopup>
        </ChatbotWidgetContainer>
      )}
    </>
  );
};
